import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { Address, countryDefaultValue } from "@models/address/address";
import { AddressStatus } from "@models/address/address-status.enums";
import { Company } from "@models/companies/company";
import { Relationship } from "@models/relationships/relationship";
import { BulkChangeData, BulkMatches, MatchesSelected } from "@models/ui-models/bulk-change-data";
import { BulkChangesGridService } from "@pages/bulk-changes/services/bulk-changes-grid.service";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import {
  AgBulkMatchesComponent
} from "@shared/components/grid/ag-cells/bulk-changes/ag-bulk-matches/ag-bulk-matches.component";
import { BaseListGridComponent } from "@shared/components/grid/components/base-list-grid/base-list-grid.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import {
  addressQueryParamKey,
  addressQueryParamSeparator,
  relationshipQueryParamKey
} from "@shared/constants/query-param-keys.constant";
import { AddressAutocompleteComponent } from "@shared/controls/address-autocomplete/address-autocomplete.component";
import { ButtonComponent } from "@shared/controls/button/button.component";
import { FlagsAutocompleteComponent } from '@shared/controls/flags-autocomplete/flags-autocomplete.component';
import { autocompleteServiceToken } from "@shared/services/autocomplete.service";
import { AuxiliaryApiService } from "@shared/services/auxiliary-api.service";
import { CountriesService } from '@shared/services/countries.service';
import { IPartialHeaderData } from "@shared/services/header.service";
import { PageSizeService } from "@shared/services/page-size.service";
import { Guid } from "@shared/utils/guid.helper";
import { RowSelectionOptions } from "ag-grid-community";
import { catchError, debounceTime, filter, Observable, of, startWith, switchMap, tap } from "rxjs";
import { BulkCompanyChangeAddressComponent } from "../../../../forms/asic-forms/bulks";
import { CompanyChangeAddress } from "../../../../forms/models/company";
import { ModalFormsService } from "../../../../forms/services/modal-forms.service";
import { BulkChangesApiService } from "../../services/bulk-changes-api.service";

interface BulkSearchControls {
  country: FormControl<string | null>;
}

@Component({
  selector: 'app-bulk-changes',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    AddressAutocompleteComponent,
    ButtonComponent,
    FlagsAutocompleteComponent,
    ListGridClientSideComponent,
  ],
  providers: [
    { provide: autocompleteServiceToken, useClass: AuxiliaryApiService },
    PageSizeService,
    BulkChangesApiService,
    BulkChangesGridService
  ],
  templateUrl: 'bulk-changes-page.component.html',
  styleUrl: 'bulk-changes-page.component.scss'
})
export class BulkChangesPageComponent extends CommonPageComponent implements OnInit {
  pageSizeService = inject(PageSizeService);
  addressService = inject(AuxiliaryApiService);
  bulkChangesApiService = inject(BulkChangesApiService);
  bulkChangesGridService = inject(BulkChangesGridService);
  route = inject(ActivatedRoute);
  modalFormsService = inject(ModalFormsService);
  destroyRef = inject(DestroyRef);
  cdr = inject(ChangeDetectorRef);

  modalOpened = this.modalFormsService.modalOpened;
  addressStatus: AddressStatus | undefined;
  selectedTrustOnly = false;
  readonly pageTitle = 'Bulk Changes';
  readonly pageInfo = 'This page will allow users to change the address for ' +
    'multiple entities (companies, trusts, and individuals). To begin, enter the entity name or address in the search field.';

  override readonly headerData: IPartialHeaderData = {
    title: this.pageTitle,
    iconClass: 'ic-bulk-changes-20'
  };

  bulkChanges = [
    {
      title: 'Change of Name',
      description: 'Change the name of an individual for multiple entities in one step. To begin, click here and search for the individual.',
      link: '/individuals'
    },
    {
      title: 'Appointment of Officeholder',
      description: 'Appoint an officeholder for multiple companies in one step. Click here, choose the companies, and click Actions.',
      link: '/companies'
    },
    {
      title: 'Cessation of Officeholder',
      description: 'Cease an officeholder for multiple companies in one step. Click here, choose the companies, and click Actions.',
      link: '/companies'
    },
    {
      title: 'Cease as a Registered Agent',
      description: 'To stop being the registered agent for multiple companies at once, click here, select the companies, and click Actions.',
      link: '/companies'
    },
  ];

  isAustraliaSelected = signal(false);
  searchControl = new FormControl<string | null>(null);
  isDisabled = false;
  showSearchBar = true;
  bulkRowData: BulkChangeData[] = [];
  bulkMatchesSelected: MatchesSelected[] = [];
  selectedRelationshipIds: string[] = [];
  form = new FormGroup<BulkSearchControls>({
    country: new FormControl<string | null>(countryDefaultValue, Validators.required),
  });

  override ngOnInit(): void {
    super.ngOnInit();
    this.setColDefs();
    this.listenCountryChanges();

    this.route.queryParams
      .pipe(
        filter(params => !!params['entityIds'] || !!params[addressQueryParamKey]),
        switchMap(params => {
          if (params['entityIds']) {
            const entityIds: string[] = [params['entityIds']].flat() as string[] ?? [];
            return this.searchByAddressAndQuery(null, '', '', entityIds);
          }

          this.selectedRelationshipIds = ([params[relationshipQueryParamKey]].flat() as string[] ?? [])
            .filter((relationshipId) => relationshipId && Guid.isValidGuid(relationshipId));

          const address = [params[addressQueryParamKey]].flat() as string[];
          const addressesString = address.join(addressQueryParamSeparator);
          this.searchControl.setValue(addressesString);
          this.textChange(addressesString);
          this.showSearchBar = address.length < 2;
          return of([]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  textChange(searchText: string): void {
    if (!searchText) {
      this.bulkMatchesSelected = [];
    }

    this.searchControl.setValue(searchText);

    const country = this.form.controls.country.value!;
    const countryCode = CountriesService.getCountryCode(country);

    of(searchText)
      .pipe(
        tap(searchText => {
          if ((searchText?.length ?? 0) < 3) {
            this.bulkRowData = [];
          }
        }),
        filter(searchText => (searchText?.length ?? 0) >= 3),
        debounceTime(1000),
        switchMap(searchText => this.searchByAddressAndQuery(null, searchText!, countryCode))
      )
      .subscribe();
  }

  parseAddress(value: Address | string | null): void {
    const country = this.form.controls.country.value!;
    const countryCode = CountriesService.getCountryCode(country);

    if (!value || value instanceof Address) {
      this.addressStatus = undefined;
      return;
    }

    this.addressService.getParseAddress(value, countryCode)
      .pipe(
        filter(({ address }) => !!address),
        tap(({ address }) => {
          if (address === null) {
            this.addressStatus = AddressStatus.WARNING;
          } else {
            this.addressStatus = AddressStatus.CHECKED;
          }
        }),
        switchMap(({ address }) => this.bulkChangesApiService.searchByAddressAndQuery(address, value, countryCode)),
        catchError((error) => {
          console.error(error);
          return of([]);
        }),
      )
      .subscribe({
        next: (bulkRowData) => {
          this.bulkRowData = bulkRowData;
        }
      });
  }

  searchByAddressAndQuery(address: Address | null, query: string, countryCode: string, entityIds: string[] = []): Observable<BulkChangeData[]> {
    return this.bulkChangesApiService.searchByAddressAndQuery(address, query, countryCode, entityIds)
      .pipe(
        catchError((error) => {
          console.error(error);
          return of([]);
        }),
        tap((res) => {
          if (this.selectedRelationshipIds.length) {
            // Select matched relationships if there are ids
            const selectedRelationshipsIds: Record<string, boolean> = this.selectedRelationshipIds
              .reduce((collection, relationshipId) => {
                collection[relationshipId] = true;
                return collection;
              }, {} as Record<string, boolean>);

            const selectedMatches: BulkMatches[] = [];
            this.bulkRowData = res
              .map((bulkChangeData: BulkChangeData) => {

                bulkChangeData.matches.forEach((match) => {
                  if (Array.isArray(match.relationshipsId) && match.relationshipsId.some(id => selectedRelationshipsIds[id])) {
                    match.value = true;
                    selectedMatches.push(match);
                  }
                });
                return bulkChangeData;
              });
            this.selectedRelationshipIds = [];
            this.matchesSelected(selectedMatches);
          } else {
            this.bulkRowData = res;
          }
        })
      );
  }

  bulkChangeAddress(): void {
    if (this.modalOpened()) return;

    const change = new CompanyChangeAddress();
    const modalRef = this.modalFormsService.openModalWithBulkCompany(change, new Company());
    const instance = modalRef.componentInstance as BulkCompanyChangeAddressComponent;

    instance.denyMinutesDocumentType = this.bulkMatchesSelected.some((match) => match.matches?.some((m) => m.denyMinutesDocumentType));
    instance.bulkMatchesSelected = this.bulkMatchesSelected;
    instance.trustOnly = this.selectedTrustOnly;
    // Registered and Principal addresses are not include relationship prop
    instance.isIndividual = this.bulkMatchesSelected.every((match) => match.matches?.every((m) => 'relationship' in m));
  }

  listenCountryChanges(): void {
    this.form.controls.country.valueChanges
      .pipe(
        startWith(this.form.controls.country.value),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((country) => {
        this.isAustraliaSelected.set(CountriesService.isAustralia(country));

        this.form.updateValueAndValidity();
        this.cdr.detectChanges();
      });
  }

  // =====  Grid  =====

  readonly bulkChangesRowSelection: RowSelectionOptions<any> = BaseListGridComponent.noCheckboxesRowSelection;
  readonly suppressRowClickSelection = true;
  readonly suppressCellFocus = true;
  readonly paginationPageSizeSelector = true;

  colDefs = this.bulkChangesGridService.colDefs;

  matchesSelected(dataMatches: BulkMatches[]): void {
    const bulkRowData = this.bulkRowData.map(bulk => {
      const relationshipIds: string[] = [];
      const relationships: Relationship[] = [];
      const matches = bulk.matches.filter(item => item.value && item.address);
      bulk.matches.forEach(item => {
        if (item.value && item.relationshipsId) {
          relationshipIds.push(...item.relationshipsId);
          if (item.relationship) {
            relationships.push(item.relationship);
          }
        }
      });

      return {
        entityId: bulk.entityId,
        acn: bulk.acn ?? '',
        abn: bulk.abn ?? '',
        name: bulk.name ?? '',
        dateOfEstablishment: bulk.dateOfEstablishment ?? '',
        matches,
        relationshipIds,
        relationships
      };
    });

    const selectedBulkRowData = bulkRowData.filter(item => item.relationshipIds.length || item.matches.length);
    this.selectedTrustOnly = selectedBulkRowData.every(item => item.abn);
    this.bulkMatchesSelected = selectedBulkRowData;
  }

  selectAllItems(isSelected: boolean): void {
    this.bulkRowData.forEach(element => {
      element.matches.forEach(m => {
        m.value = isSelected;
      });
    });
  }

  setColDefs(): void {
    this.bulkChangesGridService.setColDefs({
      headerName: 'Matches',
      field: 'matches',
      flex: 2,
      cellRenderer: AgBulkMatchesComponent,
      cellRendererParams: {
        matchesSelected: this.matchesSelected.bind(this),
      },
      headerCheckboxSelection: true,
      menuTabs: []
    });
  }
}
